import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import {
    SwapVert as SwapVertIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import TimesheetCell from './TimesheetCell';
import timesheetService from '../../services/timesheetService';
import { divisionOf, jobHeaderParts, headerCodeParts, agencyOf, shortAgency } from './gridLabels';

// Долгое нажатие на шапку работы — тот же порог, что у клеток.
const HEADER_LONG_PRESS_MS = 450;
const HEADER_MOVE_THRESHOLD = 10;

const fmtHours = (n) => Math.round(n * 100) / 100;

// Итог в углу шапки (сверху слева: повёрнутая подпись идёт из нижнего левого
// угла в верхний правый, этот угол свободен, низ справа занят кодом дня).
const totalBadgeSx = {
    position: 'absolute', top: 1, left: 1, zIndex: 1,
    px: '3px', borderRadius: '3px',
    fontSize: '0.55rem', fontWeight: 700, lineHeight: 1.45,
    bgcolor: '#e65100', color: '#fff',
    pointerEvents: 'none',
};
// Итог недели — в противоположном углу, зелёный, как колонка Week: угол
// свободен от диагональной подписи работы.
const weekBadgeSx = {
    ...totalBadgeSx,
    top: 'auto', left: 'auto', bottom: 1, right: 1,
    bgcolor: '#2e7d32',
};

// Число в бейдже шапки — короткое, чтобы не распирать узкую ячейку:
// 7.5 → 7.5, 128.25 → 128, 1061 → 1.1k, 12500 → 13k. Точное — в подсказке.
export const fmtBadge = (n) => {
    if (n >= 9999.5) return `${Math.round(n / 1000)}k`;
    if (n >= 999.5) return `${Math.round(n / 100) / 10}k`;
    if (n >= 99.5) return String(Math.round(n));
    return String(fmtHours(n));
};

export default function TimesheetTable({
    employees,
    jobs,
    selectedEmployees,
    selectedJobIds,
    records,
    weekRecords,
    currentDate,
    activeCell,
    keyboardValue,
    keyboardOpen,
    employeeSort,
    jobSortId,
    dailyNotes,
    currentDateStr,
    isReadOnly,
    currentUserId,
    otherRecords = [],
    onSetEmployeeSort,
    onToggleJobSort,
    onCellShortPress,
    onCellLongPress,
    onOpenEmployeesModal,
    onOpenRosterModal,
    onOpenJobsModal,
    onRemoveEmployee,
    onRemoveJob,
    onOpenEmployeeNotes,
    onOpenOtherHours,
}) {
    // Порядок строк (сортировка, «я первый», сортировка по работе) считает
    // TimesheetPage — тот же список ведёт клавиатуру по строкам.

    // Selected jobs objects
    const selectedJobs = useMemo(() =>
        selectedJobIds
            .map(id => jobs.find(j => j.id === id))
            .filter(j => j !== undefined),
        [jobs, selectedJobIds]
    );

    // Индексы O(1): собираем Map'ы один раз при изменении records/weekRecords.
    // Без этого .find/.filter вызывались бы на каждую из 1000+ клеток каждый рендер.
    const recordByCell = useMemo(() => {
        const map = new Map();
        for (const r of records) {
            if (r.isDeleted) continue;
            // Старый дубль клетки — последняя правленая, как и у ввода на странице.
            const key = `${r.employeeId}|${r.jobId}`;
            const cur = map.get(key);
            if (!cur || String(r.updatedAt || '') > String(cur.updatedAt || '')) map.set(key, r);
        }
        return map;
    }, [records]);

    const dayTotalByEmployee = useMemo(() => {
        const map = new Map();
        for (const r of records) {
            if (r.isDeleted) continue;
            map.set(r.employeeId, (map.get(r.employeeId) || 0) + (r.hours || 0));
        }
        return map;
    }, [records]);

    const weekTotalByEmployee = useMemo(() => {
        const map = new Map();
        for (const r of weekRecords) {
            if (r.isDeleted) continue;
            map.set(r.employeeId, (map.get(r.employeeId) || 0) + (r.hours || 0));
        }
        return map;
    }, [weekRecords]);

    // "Other" — часы других форманов по этим людям за неделю. Чужие записи на
    // айпад не едут (их правка путала часы), сервер присылает выжимку
    // otherRecords отдельно от базы записей. Сразу собираем breakdown, чтобы
    // модалка открывалась мгновенно без второго прохода по массиву.
    const otherHoursByEmployee = useMemo(() => {
        const totals = new Map();
        const breakdown = new Map();
        for (const r of otherRecords || []) {
            if (!(r.hours > 0)) continue;
            totals.set(r.employeeId, (totals.get(r.employeeId) || 0) + r.hours);
            if (!breakdown.has(r.employeeId)) breakdown.set(r.employeeId, []);
            breakdown.get(r.employeeId).push(r);
        }
        return { totals, breakdown };
    }, [otherRecords]);

    // Итоги дня в шапках: часы по каждой работе (сумма колонки) и общий итог —
    // сумма колонки Day. Считаются из тех же записей, что и клетки, поэтому
    // пересчитываются на каждое изменение сами.
    const jobDayTotals = useMemo(() => {
        const map = new Map();
        for (const emp of selectedEmployees) {
            for (const id of selectedJobIds) {
                const h = recordByCell.get(`${emp.id}|${id}`)?.hours || 0;
                if (h) map.set(id, (map.get(id) || 0) + h);
            }
        }
        return map;
    }, [selectedEmployees, selectedJobIds, recordByCell]);

    const dayGrandTotal = useMemo(
        () => selectedEmployees.reduce((s, e) => s + (dayTotalByEmployee.get(e.id) || 0), 0),
        [selectedEmployees, dayTotalByEmployee]
    );

    // Итоги недели: по работе (все дни недели, люди листа) и общий — сумма
    // колонки Week.
    const jobWeekTotals = useMemo(() => {
        const inList = new Set(selectedEmployees.map(e => e.id));
        const map = new Map();
        for (const r of weekRecords) {
            if (r.isDeleted || !inList.has(r.employeeId) || !(r.hours > 0)) continue;
            map.set(r.jobId, (map.get(r.jobId) || 0) + r.hours);
        }
        return map;
    }, [selectedEmployees, weekRecords]);

    const weekGrandTotal = useMemo(
        () => selectedEmployees.reduce((s, e) => s + (weekTotalByEmployee.get(e.id) || 0), 0),
        [selectedEmployees, weekTotalByEmployee]
    );

    // Шапка работы: тап — карточка «Remove job?», долгое нажатие — сортировка
    // листа по часам этой работы (повторное — снять). Нажатие одно на всех:
    // палец на экране всегда один.
    const hdrPress = useRef({ timer: null, long: false, start: null, tapJobId: null });
    const onHeaderDown = (e, jobId) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        const p = hdrPress.current;
        clearTimeout(p.timer);
        p.long = false;
        p.tapJobId = null;
        p.start = { x: e.clientX, y: e.clientY };
        p.timer = setTimeout(() => {
            p.timer = null;
            p.long = true;
            onToggleJobSort?.(jobId);
        }, HEADER_LONG_PRESS_MS);
    };
    const onHeaderMove = (e) => {
        const p = hdrPress.current;
        if (!p.start) return;
        const dx = e.clientX - p.start.x;
        const dy = e.clientY - p.start.y;
        if (dx * dx + dy * dy > HEADER_MOVE_THRESHOLD * HEADER_MOVE_THRESHOLD) {
            clearTimeout(p.timer);
            p.timer = null;
            p.start = null;
        }
    };
    // Карточку открываем по click, а не по pointerup: на тач-экране после
    // отрыва пальца браузер шлёт click в ту же точку, а там уже фон открытой
    // карточки — click по фону её тут же закрывал, и работу «нельзя было удалить».
    const onHeaderUp = (job) => {
        const p = hdrPress.current;
        const hadTimer = p.timer !== null;
        clearTimeout(p.timer);
        p.timer = null;
        p.tapJobId = !p.long && hadTimer && p.start ? job.id : null;
        p.start = null;
    };
    const onHeaderClick = (job) => {
        const p = hdrPress.current;
        if (p.tapJobId !== job.id) return;
        p.tapJobId = null;
        onRemoveJob(job.id, timesheetService.getJobName(job));
    };
    const onHeaderCancel = () => {
        const p = hdrPress.current;
        clearTimeout(p.timer);
        p.timer = null;
        p.start = null;
    };

    const getRecordForCell = (employeeId, jobId) => recordByCell.get(`${employeeId}|${jobId}`);
    const getHoursForCell = (employeeId, jobId) => recordByCell.get(`${employeeId}|${jobId}`)?.hours || 0;
    const getDayTotal = (employeeId) => dayTotalByEmployee.get(employeeId) || 0;
    const getWeekTotal = (employeeId) => weekTotalByEmployee.get(employeeId) || 0;
    const getOtherHours = (employeeId) => otherHoursByEmployee.totals.get(employeeId) || 0;
    const getOtherBreakdown = (employeeId) => otherHoursByEmployee.breakdown.get(employeeId) || [];

    const jobCount = selectedJobs.length;
    const isCompact = jobCount > 4;

    // Scroll indicators (horizontal + vertical) + фактическая ширина вьюпорта.
    const scrollRef = useRef(null);
    const [hScroll, setHScroll] = useState({ ratio: 0, thumbRatio: 1 });
    const [vScroll, setVScroll] = useState({ ratio: 0, thumbRatio: 1 });
    const [viewportW, setViewportW] = useState(0);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setViewportW(el.clientWidth);
        // Horizontal
        const maxH = el.scrollWidth - el.clientWidth;
        setHScroll(maxH <= 0
            ? { ratio: 0, thumbRatio: 1 }
            : { ratio: el.scrollLeft / maxH, thumbRatio: el.clientWidth / el.scrollWidth }
        );
        // Vertical
        const maxV = el.scrollHeight - el.clientHeight;
        setVScroll(maxV <= 0
            ? { ratio: 0, thumbRatio: 1 }
            : { ratio: el.scrollTop / maxV, thumbRatio: el.clientHeight / el.scrollHeight }
        );
    }, []);

    // ─── Геометрия сетки: всё измеряется, ничего не подгоняется ─────────────
    //
    // Инварианты, из которых выводится раскладка:
    //   1. Колонка Name ровно вмещает свой контент (кнопки) — её ширина
    //      МЕРЯЕТСЯ с ряда кнопок, а не задаётся константой. Кнопки не могут
    //      ни обрезаться, ни налезть на сетку.
    //   2. Ячейка работы не бывает уже CELL_MIN — ширины, в которую физически
    //      входит повёрнутая стопка «дивизион/код/клиент» ЕДИНЫМ кеглем
    //      (AABB стопки ≈ 37px). Поэтому подписи никогда не режутся и клиент
    //      виден всегда. Не хватает экрана — честный горизонтальный скролл.
    //   3. Ширина таблицы = Name + колонки, посчитана точно и передана и в
    //      width, и в colgroup: контент не может её растянуть, лишних пикселей
    //      не появляется, sticky-офсеты справа всегда совпадают с фактикой.
    const CELL_MIN = 38;
    const CELL_MAX = 54;

    const nameHdrRef = useRef(null);
    const [nameContentW, setNameContentW] = useState(0);
    useEffect(() => {
        const el = nameHdrRef.current;
        if (!el) return;
        const measure = () => setNameContentW(Math.ceil(el.scrollWidth) + 18); // + горизонтальные паддинги th
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [isReadOnly]);

    const cols = jobCount + 4; // работы + Notes/Day/Week/Other
    const nameW = Math.max(180, nameContentW);
    const cellWidth = viewportW
        ? Math.max(CELL_MIN, Math.min(CELL_MAX, Math.floor((viewportW - nameW) / cols)))
        : CELL_MIN;
    // Name добирает остаток вьюпорта, когда ячейки упёрлись в максимум —
    // чтобы справа не оставалось пустой полосы за колонкой Other.
    const nameColW = viewportW ? Math.max(nameW, viewportW - cols * cellWidth) : nameW;
    const tableW = nameColW + cols * cellWidth;

    // Стопке хватает 56px по вертикали с запасом ~10px сверху и снизу.
    const headerHeight = Math.max(cellWidth + 16, 56);
    const rowHeight = 36;

    useEffect(() => {
        handleScroll();
        const el = scrollRef.current;
        if (!el) return;
        const ro = new ResizeObserver(handleScroll);
        ro.observe(el);
        return () => ro.disconnect();
    }, [selectedJobs.length, selectedEmployees.length, handleScroll]);

    const hasHorizontalScroll = hScroll.thumbRatio < 1;
    const hasVerticalScroll = vScroll.thumbRatio < 1;
    const showScrollHint = hasHorizontalScroll || hasVerticalScroll;

    return (
        <>
        <Paper
            elevation={1}
            sx={{
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
            }}
        >
            <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
            <Box
                ref={scrollRef}
                onScroll={handleScroll}
                sx={{
                    overflow: 'auto', flex: 1,
                    WebkitOverflowScrolling: 'touch',
                    backfaceVisibility: 'hidden',
                }}
            >
                <table
                    role="grid"
                    aria-label="Timesheet grid: employees by jobs"
                    aria-rowcount={selectedEmployees.length + 1}
                    aria-colcount={selectedJobs.length + 5}
                    aria-readonly={isReadOnly || undefined}
                    style={{
                        // Точная ширина, а не 100%: контент не растягивает таблицу
                        // (fixed), и лишним пикселям взяться неоткуда — colgroup ниже
                        // расписывает ровно эту же сумму.
                        width: tableW,
                        tableLayout: 'fixed',
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                    }}
                >
                    <colgroup>
                        <col style={{ width: nameColW }} />
                        {selectedJobs.map(job => <col key={job.id} style={{ width: cellWidth }} />)}
                        <col style={{ width: cellWidth }} />
                        <col style={{ width: cellWidth }} />
                        <col style={{ width: cellWidth }} />
                        <col style={{ width: cellWidth }} />
                    </colgroup>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 30 }}>
                        <tr role="row" aria-rowindex={1}>
                            {/* Name column - sticky left + top */}
                            <Box
                                component="th"
                                role="columnheader"
                                scope="col"
                                aria-colindex={1}
                                aria-sort={employeeSort === 'asc' ? 'ascending' : employeeSort === 'desc' ? 'descending' : 'none'}
                                sx={{
                                    px: 1,
                                    py: 1,
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    position: 'sticky',
                                    left: 0,
                                    top: 0,
                                    zIndex: 31,
                                    bgcolor: 'grey.100',
                                    borderRight: '1px solid #d0d0d0',
                                    borderBottom: '2px solid #d0d0d0',
                                }}
                            >
                                {/* Один ряд слева: Name и кнопки. По ЭТОМУ ряду меряется
                                    ширина колонки Name — кнопки не могут ни обрезаться,
                                    ни налезть на сетку. Без space-between: он прижимал
                                    + Job вплотную к первой колонке работ. */}
                                <Box ref={nameHdrRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, width: 'max-content' }}>
                                    {(() => {
                                        const pillSx = {
                                            border: '1px solid',
                                            borderColor: 'primary.main',
                                            bgcolor: 'primary.50',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            color: 'primary.main',
                                            px: 0.7,
                                            py: 0.5,
                                            minHeight: 32,
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                            '&:hover': { bgcolor: 'primary.100' },
                                            '&:active': { bgcolor: 'primary.200' },
                                        };
                                        return (
                                            <>
                                                <Box
                                                    component="button"
                                                    onClick={() => {
                                                        const next = employeeSort === 'default' ? 'asc' : employeeSort === 'asc' ? 'desc' : 'default';
                                                        onSetEmployeeSort(next);
                                                    }}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        border: 'none',
                                                        bgcolor: 'transparent',
                                                        cursor: 'pointer',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        color: 'text.secondary',
                                                        p: 0,
                                                        flexShrink: 0,
                                                        '&:hover': { color: 'primary.main' },
                                                    }}
                                                >
                                                    Name
                                                    {employeeSort === 'default' && <SwapVertIcon sx={{ fontSize: 14, opacity: 0.4 }} />}
                                                    {employeeSort === 'asc' && <ArrowUpIcon sx={{ fontSize: 14, color: 'primary.main' }} />}
                                                    {employeeSort === 'desc' && <ArrowDownIcon sx={{ fontSize: 14, color: 'primary.main' }} />}
                                                </Box>
                                                {!isReadOnly && (
                                                    <>
                                                        <Box component="button" onClick={onOpenEmployeesModal} sx={pillSx}>
                                                            + Employee
                                                        </Box>
                                                        <Box component="button" onClick={onOpenRosterModal} sx={pillSx}>
                                                            + Roster
                                                        </Box>
                                                        <Box component="button" onClick={onOpenJobsModal} sx={pillSx}>
                                                            + Job
                                                        </Box>
                                                    </>
                                                )}
                                            </>
                                        );
                                    })()}
                                </Box>
                            </Box>

                            {/* Job columns — diagonal from corner to corner */}
                            {selectedJobs.map((job, jobIdx) => {
                                const hasJobNote = (dailyNotes || []).some(n =>
                                    n.jobId === job.id && !n.employeeId && n.notes
                                );
                                // Атрибут дня (job-level заметка): cost-код дня. Ночная смена
                                // теперь ПЕРСОНАЛЬНАЯ (NW-бейдж в ячейке сотрудника).
                                const dayAttr = (dailyNotes || []).find(n =>
                                    n.jobId === job.id && !n.employeeId && !n.tempId && !n.isDeleted
                                );
                                const dayCode = dayAttr?.dayJobCode || '';
                                // Подпись одной строкой вдоль диагонали: «див·код·КЛИЕНТ»
                                // («35·8199·DHG»). Полные значения — в диалоге по тапу и в title.
                                const { div: jobDiv, cust: jobCust } = jobHeaderParts(job);
                                const { lead: codeLead, main: codeMain } = headerCodeParts(timesheetService.getJobCode(job), jobDiv);
                                const jobLabel = `${timesheetService.getJobCode(job)} ${timesheetService.getJobName(job)}`;
                                const jobTotal = jobDayTotals.get(job.id) || 0;
                                const jobWeek = jobWeekTotals.get(job.id) || 0;
                                const isSortedBy = jobSortId === job.id;
                                return (
                                    <Box component="th" key={job.id}
                                        role="columnheader"
                                        scope="col"
                                        aria-colindex={jobIdx + 2}
                                        aria-sort={isSortedBy ? 'descending' : undefined}
                                        aria-label={`${jobLabel}${jobDiv ? ', division ' + jobDiv : ''}${jobCust ? ', ' + jobCust : ''}${dayCode ? ', day code ' + dayCode : ''}${jobTotal ? `, ${fmtHours(jobTotal)} hours today` : ''}${jobWeek ? `, ${fmtHours(jobWeek)} hours this week` : ''}`}
                                        onPointerDown={(e) => onHeaderDown(e, job.id)}
                                        onPointerMove={onHeaderMove}
                                        onPointerUp={() => onHeaderUp(job)}
                                        onClick={() => onHeaderClick(job)}
                                        onPointerCancel={onHeaderCancel}
                                        onPointerLeave={onHeaderCancel}
                                        onContextMenu={(e) => e.preventDefault()}
                                        sx={{
                                            p: 0, width: cellWidth, minWidth: cellWidth, height: headerHeight,
                                            // У последней работы правой линии нет — иначе вместе с
                                            // тёмным левым разделителем Notes выходила двойная граница.
                                            borderRight: jobIdx === selectedJobs.length - 1 ? 'none' : '1px solid #d0d0d0',
                                            borderBottom: isSortedBy ? '3px solid #1976d2' : '2px solid #d0d0d0',
                                            bgcolor: isSortedBy ? '#dbeafe' : hasJobNote ? '#ede9fe' : 'grey.100',
                                            cursor: 'pointer', position: 'relative', overflow: 'hidden',
                                            userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
                                            touchAction: 'manipulation',
                                            ...(hasJobNote && { border: '2px solid #7c3aed' }),
                                            ...(isSortedBy && { borderBottom: '3px solid #1976d2' }),
                                            '&:hover': { bgcolor: '#fee2e2' },
                                        }}
                                        title={`${timesheetService.getJobCode(job)} — ${timesheetService.getJobName(job)}${divisionOf(job) ? ` · div ${divisionOf(job)}` : ''}${timesheetService.getJobCustomer(job) ? ` · ${timesheetService.getJobCustomer(job)}` : ''}${dayCode ? ` · day code ${dayCode}` : ''}${hasJobNote ? ' ★ job note' : ''}${jobTotal ? `\n${fmtHours(jobTotal)} h on this job today` : ''}${jobWeek ? `\n${fmtHours(jobWeek)} h on this job this week` : ''}\nTap for details · hold to sort the list by this job's hours`}
                                    >
                                        {jobTotal > 0 && (
                                            <Box sx={totalBadgeSx}>{fmtBadge(jobTotal)}</Box>
                                        )}
                                        {jobWeek > 0 && (
                                            <Box sx={weekBadgeSx}>{fmtBadge(jobWeek)}</Box>
                                        )}
                                        {/* Повёрнутая на 45° стопка: дивизион, КОД, клиент — вдоль
                                            диагонали сверху вниз, все три строки ВСЕГДА. Кегли
                                            зависят от ширины ячейки, чтобы стопка сидела внутри и
                                            буквы не резались границами (минимум ширины — 32px,
                                            дальше сетка честно уходит в скролл). */}
                                        <Box sx={{
                                            position: 'absolute', inset: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transform: 'rotate(-45deg)',
                                            color: hasJobNote ? '#7c3aed' : 'text.secondary',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {/* Кегль ЕДИНЫЙ на любой ширине: CELL_MIN выведен из
                                                размера этой стопки, а не наоборот. */}
                                            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 0 }}>
                                                {(jobDiv || codeLead) && (
                                                    <Box sx={{ fontSize: '0.52rem', fontWeight: 600, opacity: 0.7, lineHeight: 1 }}>
                                                        {jobDiv || codeLead}
                                                    </Box>
                                                )}
                                                <Box sx={{ fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.1 }}>
                                                    {codeMain}
                                                </Box>
                                                {jobCust && (
                                                    <Box sx={{ fontSize: '0.52rem', fontWeight: 600, opacity: 0.7, lineHeight: 1 }}>
                                                        {jobCust}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                        {dayCode && (
                                            <Box sx={{
                                                position: 'absolute', bottom: 1, right: 1, zIndex: 1,
                                                px: '2px', borderRadius: '2px', fontSize: '0.5rem', fontWeight: 700,
                                                bgcolor: '#e0f2fe', color: '#0369a1', lineHeight: 1.4,
                                            }}>
                                                {dayCode}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}

                            {/* Notes — sticky right (4th from right) */}
                            <Box component="th"
                                role="columnheader" scope="col"
                                aria-colindex={selectedJobs.length + 2}
                                aria-label="Notes"
                                sx={{
                                p: 0, width: cellWidth, minWidth: cellWidth, height: headerHeight,
                                position: 'sticky', right: cellWidth * 3, top: 0, zIndex: 31,
                                borderRight: '1px solid #d0d0d0', borderBottom: '2px solid #d0d0d0',
                                borderLeft: '1px solid #94a3b8',
                                bgcolor: 'grey.100', overflow: 'hidden',
                            }}>
                                <Box sx={{
                                    position: 'absolute', inset: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transform: 'rotate(-45deg)',
                                    fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap',
                                }}>
                                    Notes
                                </Box>
                            </Box>

                            {/* Day — sticky right */}
                            <Box component="th"
                                role="columnheader" scope="col"
                                aria-colindex={selectedJobs.length + 3}
                                aria-label="Day total"
                                sx={{
                                p: 0, width: cellWidth, minWidth: cellWidth, height: headerHeight,
                                position: 'sticky', right: cellWidth * 2, top: 0, zIndex: 31,
                                borderRight: '1px solid #d0d0d0', borderBottom: '2px solid #d0d0d0',
                                bgcolor: 'grey.100', overflow: 'hidden',
                            }}
                                title={dayGrandTotal > 0 ? `${fmtHours(dayGrandTotal)} h today for everyone in the list` : undefined}
                            >
                                {dayGrandTotal > 0 && (
                                    <Box sx={totalBadgeSx}>{fmtBadge(dayGrandTotal)}</Box>
                                )}
                                <Box sx={{
                                    position: 'absolute', inset: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transform: 'rotate(-45deg)',
                                    fontSize: '0.65rem', fontWeight: 700, color: '#e65100', whiteSpace: 'nowrap',
                                }}>
                                    Day
                                </Box>
                            </Box>

                            {/* Week — sticky right */}
                            <Box component="th"
                                role="columnheader" scope="col"
                                aria-colindex={selectedJobs.length + 4}
                                aria-label={weekGrandTotal > 0 ? `Week total, ${fmtHours(weekGrandTotal)} hours for everyone in the list` : 'Week total'}
                                sx={{
                                p: 0, width: cellWidth, minWidth: cellWidth, height: headerHeight,
                                position: 'sticky', right: cellWidth, top: 0, zIndex: 31,
                                borderRight: '1px solid #d0d0d0', borderBottom: '2px solid #d0d0d0',
                                bgcolor: 'grey.100', overflow: 'hidden',
                            }}
                                title={weekGrandTotal > 0 ? `${fmtHours(weekGrandTotal)} h this week for everyone in the list` : undefined}
                            >
                                {weekGrandTotal > 0 && (
                                    <Box sx={weekBadgeSx}>{fmtBadge(weekGrandTotal)}</Box>
                                )}
                                <Box sx={{
                                    position: 'absolute', inset: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transform: 'rotate(-45deg)',
                                    fontSize: '0.65rem', fontWeight: 700, color: '#2e7d32', whiteSpace: 'nowrap',
                                }}>
                                    Week
                                </Box>
                            </Box>

                            {/* Other — sticky right (1st from right) — часы от других бригадиров */}
                            <Box component="th"
                                role="columnheader" scope="col"
                                aria-colindex={selectedJobs.length + 5}
                                aria-label="Hours from other foremen"
                                sx={{
                                p: 0, width: cellWidth, minWidth: cellWidth, height: headerHeight,
                                position: 'sticky', right: 0, top: 0, zIndex: 31,
                                borderRight: '1px solid #d0d0d0', borderBottom: '2px solid #d0d0d0',
                                borderLeft: '1px solid #94a3b8',
                                bgcolor: 'grey.100', overflow: 'hidden',
                            }}
                                title="Hours recorded for this employee by other foremen"
                            >
                                <Box sx={{
                                    position: 'absolute', inset: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transform: 'rotate(-45deg)',
                                    fontSize: '0.65rem', fontWeight: 700, color: '#c2410c', whiteSpace: 'nowrap',
                                }}>
                                    Other
                                </Box>
                            </Box>
                        </tr>
                    </thead>

                    <tbody>
                        {selectedEmployees.map((employee, rowIdx) => {
                            const dayTotal = getDayTotal(employee.id);
                            const weekTotal = getWeekTotal(employee.id);
                            const otherHours = getOtherHours(employee.id);
                            const hasAnyNotes = records.some(r => r.employeeId === employee.id && r.notes);
                            const empName = timesheetService.getEmployeeName(employee);
                            const empDivision = divisionOf(employee);
                            // У temp'а агентство первыми буквами — рядом с дивизионом, если он есть
                            // (Temps.Division заведён не у всех), иначе вместо него.
                            const empAgency = employee._type === 'temp' ? agencyOf(employee) : '';

                            return (
                                <tr key={employee.id} role="row" aria-rowindex={rowIdx + 2}>
                                    {/* Employee name - sticky */}
                                    <Box
                                        component="td"
                                        role="rowheader"
                                        aria-colindex={1}
                                        sx={{
                                            px: 0.5,
                                            py: 0,
                                            fontWeight: 500,
                                            fontSize: '0.7rem',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 10,
                                            bgcolor: rowIdx % 2 === 0 ? 'background.paper' : 'grey.50',
                                            whiteSpace: 'nowrap',
                                            borderRight: '1px solid #d0d0d0',
                                            borderBottom: '1px solid #d0d0d0',
                                            minWidth: 100,
                                            height: rowHeight,
                                        }}
                                    >
                                        <Box
                                            component="button"
                                            onClick={() => onRemoveEmployee(employee.id, empName)}
                                            onDoubleClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onOpenEmployeeNotes(employee.id);
                                            }}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                border: 'none',
                                                bgcolor: 'transparent',
                                                cursor: 'pointer',
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                                color: 'text.primary',
                                                p: 0,
                                                textAlign: 'left',
                                                '&:hover': { color: 'error.main' },
                                            }}
                                            title={`${empName}${empDivision ? ` · div ${empDivision}` : ''}${empAgency ? ` · temp, ${empAgency}` : ''}\nClick to remove, double-click for notes`}
                                        >
                                            {empName}
                                            {/* Дивизион сотрудника — тихим чипом: он нужен глазу
                                                для группировки, но не должен спорить с именем. */}
                                            {empDivision && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        px: 0.4,
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: '4px',
                                                        color: '#64748b',
                                                        fontSize: '0.58rem',
                                                        fontWeight: 700,
                                                        lineHeight: 1.5,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {empDivision}
                                                </Box>
                                            )}
                                            {/* Агентство temp'а — тот же чип, оранжевый, как метка temp в окне выбора. */}
                                            {empAgency && (
                                                <Box
                                                    component="span"
                                                    title={`Temp · ${empAgency}`}
                                                    sx={{
                                                        px: 0.4,
                                                        border: '1px solid #fdba74',
                                                        borderRadius: '4px',
                                                        color: '#c2410c',
                                                        fontSize: '0.58rem',
                                                        fontWeight: 700,
                                                        lineHeight: 1.5,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {shortAgency(empAgency)}
                                                </Box>
                                            )}
                                            {hasAnyNotes && (
                                                <Box
                                                    sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        bgcolor: 'warning.main',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Hours cells */}
                                    {selectedJobs.map((job, colIdx) => {
                                        const hours = getHoursForCell(employee.id, job.id);
                                        const record = getRecordForCell(employee.id, job.id);
                                        const isActive = activeCell?.employeeId === employee.id && activeCell?.jobId === job.id;

                                        return (
                                            <TimesheetCell
                                                key={job.id}
                                                employeeId={employee.id}
                                                jobId={job.id}
                                                employeeIndex={rowIdx}
                                                jobIndex={colIdx}
                                                hours={hours}
                                                editingValue={isActive ? keyboardValue : undefined}
                                                syncStatus={record?.syncStatus}
                                                workflowStatus={record?.status}
                                                rejectionReason={record?.rejectionReason}
                                                rejectedByName={record?.rejectedByName}
                                                hasNotes={!!record?.notes}
                                                attendCode={record?.attendCode || ''}
                                                isCompact={isCompact}
                                                cellSize={cellWidth}
                                                isActive={isActive}
                                                isReadOnly={isReadOnly}
                                                isLastCol={colIdx === selectedJobs.length - 1}
                                                onShortPress={onCellShortPress}
                                                onLongPress={onCellLongPress}
                                            />
                                        );
                                    })}

                                    {/* Notes indicator — click opens EmployeeNotesModal */}
                                    {(() => {
                                        const hasCellNotes = records.some(r => r.employeeId === employee.id && r.notes && !r.isDeleted);
                                        const hasGeneralNote = (dailyNotes || []).some(n => n.employeeId === employee.id && !n.jobId && n.notes);
                                        const hasAny = hasCellNotes || hasGeneralNote || hasAnyNotes;
                                        return (
                                            <Box
                                                component="td"
                                                onClick={() => onOpenEmployeeNotes(employee.id)}
                                                sx={{
                                                    textAlign: 'center',
                                                    borderRight: '1px solid #d0d0d0',
                                                    borderBottom: '1px solid #d0d0d0',
                                                    borderLeft: '1px solid #94a3b8',
                                                    cursor: 'pointer',
                                                    height: rowHeight,
                                                    width: cellWidth,
                                                    minWidth: cellWidth,
                                                    position: 'sticky',
                                                    right: cellWidth * 3,
                                                    zIndex: 10,
                                                    bgcolor: rowIdx % 2 === 0 ? '#fff' : '#fafafa',
                                                    '&:hover': { bgcolor: '#f0f0f0' },
                                                }}
                                            >
                                                {hasAny ? (
                                                    <Box sx={{ display: 'flex', gap: '3px', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                        {hasCellNotes && (
                                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ea580c' }} title="Employee notes" />
                                                        )}
                                                        {hasGeneralNote && (
                                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2563eb' }} title="Employee day note" />
                                                        )}
                                                        {!hasCellNotes && !hasGeneralNote && hasAnyNotes && (
                                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ea580c' }} title="Has notes" />
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>-</Box>
                                                )}
                                            </Box>
                                        );
                                    })()}

                                    {/* Day Total - sticky right */}
                                    <Box
                                        component="td"
                                        sx={{
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            color: dayTotal > 0 ? '#e65100' : 'text.disabled',
                                            position: 'sticky',
                                            right: cellWidth * 2,
                                            zIndex: 10,
                                            bgcolor: rowIdx % 2 === 0 ? '#fff' : '#fafafa',
                                            borderRight: '1px solid #d0d0d0', borderBottom: '1px solid #d0d0d0',
                                            width: cellWidth,
                                            minWidth: cellWidth,
                                            height: rowHeight,
                                        }}
                                    >
                                        {dayTotal > 0 ? dayTotal : ''}
                                    </Box>

                                    {/* Week Total - sticky right */}
                                    <Box
                                        component="td"
                                        sx={{
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            color: weekTotal > 0 ? '#2e7d32' : 'text.disabled',
                                            position: 'sticky',
                                            right: cellWidth,
                                            zIndex: 10,
                                            bgcolor: rowIdx % 2 === 0 ? '#fff' : '#fafafa',
                                            borderRight: '1px solid #d0d0d0', borderBottom: '1px solid #d0d0d0',
                                            width: cellWidth,
                                            minWidth: cellWidth,
                                            height: rowHeight,
                                        }}
                                    >
                                        {weekTotal > 0 ? weekTotal : ''}
                                    </Box>

                                    {/* Other (от других бригадиров) — sticky right, кликабельно */}
                                    <Box
                                        component="td"
                                        onClick={otherHours > 0
                                            ? () => onOpenOtherHours && onOpenOtherHours(
                                                employee.id,
                                                empName,
                                                getOtherBreakdown(employee.id),
                                            )
                                            : undefined}
                                        title={otherHours > 0
                                            ? `${otherHours}h recorded by other foremen — tap for details`
                                            : 'No hours from other foremen this week'}
                                        sx={{
                                            textAlign: 'center',
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            color: otherHours > 0 ? '#c2410c' : 'text.disabled',
                                            position: 'sticky',
                                            right: 0,
                                            zIndex: 10,
                                            bgcolor: otherHours > 0
                                                ? '#fff7ed'
                                                : (rowIdx % 2 === 0 ? '#fff' : '#fafafa'),
                                            borderRight: '1px solid #d0d0d0',
                                            borderBottom: '1px solid #d0d0d0',
                                            borderLeft: '1px solid #94a3b8',
                                            width: cellWidth,
                                            minWidth: cellWidth,
                                            height: rowHeight,
                                            cursor: otherHours > 0 ? 'pointer' : 'default',
                                            '&:hover': otherHours > 0 ? { bgcolor: '#fed7aa' } : undefined,
                                        }}
                                    >
                                        {otherHours > 0 ? otherHours : ''}
                                    </Box>
                                </tr>
                            );
                        })}

                    </tbody>
                </table>
            </Box>

            {/* Vertical scroll indicator — overlaps right border, doesn't take space */}
            {hasVerticalScroll && (
                <Box sx={{
                    position: 'absolute', right: -2, top: 0, bottom: 0,
                    width: 4, zIndex: 20, pointerEvents: 'none',
                }}>
                    <Box sx={{
                        position: 'absolute', right: 0,
                        top: `${vScroll.ratio * (1 - vScroll.thumbRatio) * 100}%`,
                        width: '100%',
                        height: `${Math.max(vScroll.thumbRatio * 100, 10)}%`,
                        bgcolor: '#475569', borderRadius: 1,
                        transition: 'top 0.05s',
                    }} />
                </Box>
            )}
            </Box>

            {/* Fixed footer — only when scrolling is needed */}
            {showScrollHint && (
                <Box sx={{ flexShrink: 0, borderTop: '1px solid #d0d0d0' }}>
                    {hasHorizontalScroll && (
                        <Box sx={{ height: 8, bgcolor: '#e2e8f0', position: 'relative' }}>
                            <Box sx={{
                                position: 'absolute', top: 0,
                                left: `${hScroll.ratio * (1 - hScroll.thumbRatio) * 100}%`,
                                width: `${hScroll.thumbRatio * 100}%`,
                                height: '100%', bgcolor: '#475569', borderRadius: 1,
                                transition: 'left 0.05s',
                            }} />
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5 }}>
                        <Box sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                            {hasHorizontalScroll && '↔'}{hasVerticalScroll && '↕'} scroll to see all · rotate for more space
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Empty state */}
            {selectedEmployees.length === 0 && selectedJobs.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        No employees or jobs selected
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onOpenEmployeesModal}
                            disabled={isReadOnly}
                        >
                            Select Employees
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onOpenRosterModal}
                            disabled={isReadOnly}
                        >
                            From Weekly Roster
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onOpenJobsModal}
                            disabled={isReadOnly}
                        >
                            Select Jobs
                        </Button>
                    </Box>
                </Box>
            )}
        </Paper>
        </>
    );
}
